import { useUserStorage } from '@common/hooks/useUserStorage';
import { authService, type VerifyAuthResponse } from '@common/api/authService';
import {
    useCallback, useEffect, useRef, useState
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import ConnectShopify, { CONNECT_SHOPIFY_STORAGE_KEY, ShopifyStore } from '../../components/ConnectShopify';
import {
    ForNextPulseModal,
    InviteSuccessModal,
    PaymentSuccessModal,
    type PaymentSuccessKind,
    PaySuccessModal,
    PricingModal
} from '../../components/Pricing';
import {
    DIRECT_SUBSCRIPTION_PAYMENT_SUCCESS_EVENT,
    type DirectSubscriptionPaymentSuccessDetail,
} from '../../components/Pricing/trialActivationUtils';
import SettingsModal from '../../components/Settings/SettingsModal';
import { useAuth } from '../AuthContext';
import {
    buildOnboardingPaymentSuccessParams,
    PAYMENT_RESULT_PARAMS_TO_CLEAR,
    readPaymentReturnParams,
    shouldClearPayResultImmediately,
    shouldFinalizeOnboardingPaymentSuccess,
    resolvePaymentWorkspaceAction
} from './paymentResultUtils';
import { refreshProfileFromServer } from '@/contexts/AuthContext/refreshProfileFromServer';
import { removeSearchParams } from './searchParamsUtils';
import {
    clearPaymentReturnWorkspaceId,
    readPaymentReturnWorkspaceId,
} from '@/utils/paymentReturnWorkspace';
import { applyPaymentReturnPlanToProfile } from '@/contexts/AuthContext/paymentReturnPlanSync';
import { captureSentryError, logSentryEvent } from '@/lib/sentry';
import { getRouteBehavior, isAdminLayoutGlobalLoadingOwner } from '@/routeBehavior';
import {
    INVITE_SUCCESS_PARAMS_TO_CLEAR,
    readInviteSuccessReturnParams,
    resolveInviteSuccessWorkspaceAction,
} from '@/components/Onboarding/inviteReturn';

export interface ModalConfig {
    isOpen: boolean;
    activeTab?: string;
    onClose?: () => void;
    data?: any;
}

export interface ModalState {
    modals: Record<string, ModalConfig>;
    openModal: (modalId: string, config?: Partial<ModalConfig>) => void;
    closeModal: (modalId: string) => void;
    toggleModal: (modalId: string, config?: Partial<ModalConfig>) => void;
    updateModal: (modalId: string, config: Partial<ModalConfig>) => void;
    isModalOpen: (modalId: string) => boolean;
    getModalConfig: (modalId: string) => ModalConfig;
}

export const modalInitialState: ModalState = {
    modals: {},
    openModal: () => {},
    closeModal: () => {},
    toggleModal: () => {},
    updateModal: () => {},
    isModalOpen: () => false,
    getModalConfig: () => ({ isOpen: false }),
};

export const modalInitialModalState: ModalState = modalInitialState;

const WORKSPACE_SCOPED_SETTING_TABS = new Set([
    'integration',
    'workspace-settings',
    'members',
    'billing',
    'usage',
]);

const PAID_PRODUCT_PLAN_CODES = new Set([
    'SOLO_199',
    'SOLO_399',
    'BASIC',
    'SOLO_999',
    'SOLO_1299',
    'SOLO',
    'TEAM_1499',
    'TEAM_2999',
    'PRO',
    'TEAM_9999',
    'TEAM_49999',
    'TEAM',
]);

const getFeishuOAuthRedirectUri = () => {
    const supabaseUrl = String(import.meta.env.VITE_SUPABASE_URL || '').replace(/\/$/, '');
    return supabaseUrl ? `${supabaseUrl}/functions/v1/feishu-oauth-callback` : '';
};

const resolveProductPaymentSuccessKind = (
    profile: VerifyAuthResponse | null,
): PaymentSuccessKind => {
    const currentPlanCode = profile?.workspacePlan?.planCode?.trim().toUpperCase();
    const currentPlanStatus = profile?.workspacePlan?.status?.trim().toLowerCase();

    if (
        currentPlanCode
        && PAID_PRODUCT_PLAN_CODES.has(currentPlanCode)
        && currentPlanStatus === 'active'
    ) {
        return 'upgrade';
    }

    return 'subscription';
};

const readSearchParam = (
    searchParams: URLSearchParams,
    keys: string[],
): string | null => {
    for (const key of keys) {
        const value = searchParams.get(key);
        if (value != null && value !== '') {
            return value;
        }
    }

    return null;
};

const readBooleanSearchParam = (
    searchParams: URLSearchParams,
    keys: string[],
): boolean | undefined => {
    const value = readSearchParam(searchParams, keys);

    if (value == null) {
        return undefined;
    }

    if (value === 'true' || value === '1') {
        return true;
    }

    if (value === 'false' || value === '0') {
        return false;
    }

    return undefined;
};

const readNumberSearchParam = (
    searchParams: URLSearchParams,
    keys: string[],
): number | null | undefined => {
    const value = readSearchParam(searchParams, keys);

    if (value == null) {
        return undefined;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const buildConnectCallbackPayload = (searchParams: URLSearchParams) => {
    const restrictionState = readSearchParam(searchParams, ['restrictionState', 'restriction_state']);
    const eligibilityState = readSearchParam(searchParams, ['eligibilityState', 'eligibility_state']);
    const blockConnect = readBooleanSearchParam(searchParams, ['blockConnect', 'block_connect']);
    const upgradeRequired = readBooleanSearchParam(searchParams, ['upgradeRequired', 'upgrade_required']);
    const allowsFreshTrial = readBooleanSearchParam(searchParams, ['allowsFreshTrial', 'allows_fresh_trial']);
    const message = readSearchParam(searchParams, ['message']);
    const eligibilityMessage = readSearchParam(searchParams, ['eligibilityMessage', 'eligibility_message']);
    const trialRemainingDays = readNumberSearchParam(searchParams, ['trialRemainingDays', 'trial_remaining_days']);
    const currentWorkspaceId = readSearchParam(searchParams, ['currentWorkspaceId', 'current_workspace_id']);
    const currentWorkspaceName = readSearchParam(searchParams, ['currentWorkspaceName', 'current_workspace_name']);
    const currentStoreCount = readNumberSearchParam(searchParams, ['currentStoreCount', 'current_store_count']);
    const storeLimit = readNumberSearchParam(searchParams, ['storeLimit', 'store_limit']);
    const upgradeTarget = readSearchParam(searchParams, ['upgradeTarget', 'upgrade_target']);

    const payload = {
        restrictionState,
        eligibilityState,
        blockConnect,
        upgradeRequired,
        allowsFreshTrial,
        message,
        eligibilityMessage,
        trialRemainingDays,
        currentWorkspaceId,
        currentWorkspaceName,
        currentStoreCount,
        storeLimit,
        upgradeTarget,
    };

    return Object.values(payload).some((value) => value !== undefined && value !== null && value !== '')
        ? payload
        : undefined;
};

export const createModalProvider = () => {
    const [modals, setModals] = useState<Record<string, ModalConfig>>({});
    const [stores, setStores] = useLocalStorage<ShopifyStore[]>(CONNECT_SHOPIFY_STORAGE_KEY, []);
    const { get, set } = useUserStorage();
    const {
        showGlobalLoading,
        hideGlobalLoading,
        currentWorkspace,
        isLoadingWorkspaces,
        profile,
        setProfile,
        userBilling,
        refreshWorkspaces,
        switchWorkspace,
        applyWorkspaceSubscriptionPlan,
    } = useAuth();

    const [searchParams, setSearchParams] = useSearchParams();
    const handledPaymentResultKeyRef = useRef<string | null>(null);
    const handledConnectResultKeyRef = useRef<string | null>(null);
    const handledInviteSuccessKeyRef = useRef<string | null>(null);
    const profileRef = useRef(profile);
    const userBillingRef = useRef(userBilling);

    useEffect(() => {
        profileRef.current = profile;
    }, [profile]);

    useEffect(() => {
        userBillingRef.current = userBilling;
    }, [userBilling]);

    const replaceSearchParams = useCallback((mutate: (params: URLSearchParams) => void) => {
        const nextParams = new URLSearchParams(searchParams);
        mutate(nextParams);
        setSearchParams(nextParams, { replace: true });
    }, [searchParams, setSearchParams]);

    const openModal = useCallback((modalId: string, config?: Partial<ModalConfig>) => {
        // 特殊处理 forNextPulse modal：检查是否已经显示过
        if (modalId === 'forNextPulse') {
            const FOR_NEXT_PULSE_SHOWN_KEY = 'for_next_pulse_shown';
            const hasShown = get(FOR_NEXT_PULSE_SHOWN_KEY, false);
            if (hasShown) {
                return; // 已经显示过，不再打开
            }
            set(FOR_NEXT_PULSE_SHOWN_KEY, true);
        }

        const resolvedData = modalId === 'pricing'
            ? {
                workspaceId: currentWorkspace?.id || '',
                ...config?.data,
            }
            : config?.data;

        setModals(prev => ({
            ...prev,
            [modalId]: {
                isOpen: true,
                activeTab: config?.activeTab,
                onClose: config?.onClose,
                ...config,
                data: resolvedData,
            },
        }));
    }, [get, set, currentWorkspace?.id]);

    const closeModal = useCallback((modalId: string) => {
        setModals(prev => {
            const modal = prev[modalId];
            if (modal?.onClose) {
                modal.onClose();
            }
            return {
                ...prev,
                [modalId]: {
                    ...modal,
                    isOpen: false,
                },
            };
        });
    }, []);

    const toggleModal = useCallback((modalId: string, config?: Partial<ModalConfig>) => {
        setModals(prev => {
            const modal = prev[modalId] || { isOpen: false };
            const newState = !modal.isOpen;
            return {
                ...prev,
                [modalId]: {
                    ...modal,
                    ...config,
                    isOpen: newState,
                },
            };
        });
    }, []);

    const updateModal = useCallback((modalId: string, config: Partial<ModalConfig>) => {
        setModals(prev => {
            const modal = prev[modalId] || { isOpen: false };
            return {
                ...prev,
                [modalId]: {
                    ...modal,
                    ...config,
                },
            };
        });
    }, []);

    const isModalOpen = useCallback((modalId: string) => {
        const modal = modals[modalId];
        return modal?.isOpen || false;
    }, [modals]);

    const getModalConfig = useCallback((modalId: string) => {
        return modals[modalId] || { isOpen: false };
    }, [modals]);

    useEffect(() => {
        const handleDirectSubscriptionPaymentSuccess = (event: Event) => {
            const detail = ((event as CustomEvent<DirectSubscriptionPaymentSuccessDetail>).detail || {}) as DirectSubscriptionPaymentSuccessDetail;

            closeModal('pricing');
            openModal('paymentSuccess', {
                data: {
                    kind: detail.kind || 'upgrade',
                    planCode: detail.planCode,
                    planName: detail.planName,
                },
            });
        };

        window.addEventListener(DIRECT_SUBSCRIPTION_PAYMENT_SUCCESS_EVENT, handleDirectSubscriptionPaymentSuccess);
        return () => {
            window.removeEventListener(DIRECT_SUBSCRIPTION_PAYMENT_SUCCESS_EVENT, handleDirectSubscriptionPaymentSuccess);
        };
    }, [closeModal, openModal]);

    useEffect(() => {
        const connectSuccess = searchParams.get('connect_success');
        if (!connectSuccess) {
            handledConnectResultKeyRef.current = null;
            return;
        }

        const connectKey = `${connectSuccess}|${searchParams.toString()}`;
        if (handledConnectResultKeyRef.current === connectKey) {
            return;
        }
        handledConnectResultKeyRef.current = connectKey;

        const channel = new BroadcastChannel('connect');
        const payload = buildConnectCallbackPayload(searchParams);
        const callbackProvider = searchParams.get('type') || connectSuccess;

        const postConnectionResult = (payload: Record<string, unknown>) => {
            channel.postMessage(payload);
            if (window.opener) {
                try {
                    window.opener.postMessage(payload, window.location.origin);
                } catch {}
            }
        };

        const buildFeishuSuccessPayload = () => ({
            source: 'bustly-feishu',
            type: 'SUCCESS',
            provider: 'feishu',
            region: searchParams.get('region'),
            connection: searchParams.get('connection'),
            integration_id: searchParams.get('integration_id'),
            tenant_key: searchParams.get('tenant_key'),
            tenant_name: searchParams.get('tenant_name'),
        });

        const buildErrorPayload = (message: string) => (
            callbackProvider === 'feishu'
                ? {
                    source: 'bustly-feishu',
                    type: 'ERROR',
                    provider: 'feishu',
                    message,
                }
                : {
                    type: 'ERROR',
                    provider: connectSuccess,
                    message,
                }
        );

        (async () => {
            const connectError = searchParams.get('connect_error');
            if (connectError) {
                throw new Error(connectError);
            }

            if (connectSuccess === 'wix') {
                const workspaceId = searchParams.get('workspace_id') || currentWorkspace?.id;
                if (!workspaceId) {
                    throw new Error('Missing workspace_id for Wix callback');
                }

                const callbackParams = Object.fromEntries(searchParams.entries());

                await authService.completeWixConnect({
                    workspace_id: workspaceId,
                    callback_params: callbackParams,
                    callback_url: window.location.href
                });
            }

            if (connectSuccess === 'feishu') {
                const code = searchParams.get('code');
                const state = searchParams.get('state');
                if (code && state) {
                    const redirectUri = getFeishuOAuthRedirectUri();
                    if (!redirectUri) {
                        throw new Error('Missing Feishu OAuth redirect URI');
                    }
                    const feishuResult = await authService.completeFeishuConnect({
                        code,
                        state,
                        redirect_uri: redirectUri
                    });
                    if (!feishuResult.ok) {
                        throw new Error(feishuResult.message || 'Failed to complete Feishu callback');
                    }
                } else if (!searchParams.get('connection')) {
                    throw new Error('Missing Feishu callback completion marker');
                }
            }

            postConnectionResult(connectSuccess === 'feishu'
                ? buildFeishuSuccessPayload()
                : {
                    type: 'SUCCESS',
                    provider: connectSuccess,
                    payload,
                });
        })().catch((error: any) => {
            postConnectionResult(buildErrorPayload(error?.message || `Failed to complete ${callbackProvider} callback`));
        }).finally(() => {
            channel.close();
            window.close();
        });
    }, [searchParams, currentWorkspace?.id]);

    // 处理支付结果 - 检查 URL 中的 pay_result 参数
    useEffect(() => {
        const paymentReturn = readPaymentReturnParams(searchParams, {
            workspaceId: readPaymentReturnWorkspaceId(),
        });
        const pay_result = paymentReturn.result;
        if (pay_result !== 'success' && pay_result !== 'cancel') {
            handledPaymentResultKeyRef.current = null;
            return;
        }

        let cancelled = false;

        const handlePaymentResult = async () => {
            const requestedWorkspaceId = paymentReturn.workspaceId;
            const currentWorkspaceId = currentWorkspace?.id ?? null;
            const workspaceAction = resolvePaymentWorkspaceAction(
                pay_result,
                requestedWorkspaceId,
                currentWorkspaceId,
                isLoadingWorkspaces
            );
            logSentryEvent('payment.return.detected', {
                area: 'payment',
                pay_result,
                requested_workspace_id: requestedWorkspaceId,
                current_workspace_id: currentWorkspaceId,
                workspace_action: workspaceAction,
                plan_code: paymentReturn.planCode,
                payment_checked: paymentReturn.checked,
            });
            const paymentResultKey = [
                window.location.pathname,
                pay_result,
                requestedWorkspaceId || '',
                currentWorkspaceId || '',
                paymentReturn.planCode || '',
                paymentReturn.planName || '',
                paymentReturn.checked ? 'checked' : 'unchecked',
            ].join('|');

            if (workspaceAction === 'wait') {
                // useWorkspaces is the single source of truth for consuming
                // workspace_id from the URL and syncing current workspace.
                // We only continue once that sync has completed.
                if (isAdminLayoutGlobalLoadingOwner(window.location.pathname)) {
                    showGlobalLoading();
                } else {
                    hideGlobalLoading();
                }
                if (pay_result === 'success') {
                    try {
                        await refreshWorkspaces({
                            silent: true,
                            skipEmptyRedirect: true,
                            preferredWorkspaceId: requestedWorkspaceId || undefined,
                        });
                    } catch (error) {
                        logSentryEvent('payment.return.workspace_refresh_failed', {
                            area: 'payment',
                            requested_workspace_id: requestedWorkspaceId,
                            current_workspace_id: currentWorkspaceId,
                        }, 'error');
                        captureSentryError(error, {
                            area: 'payment',
                            flow: 'payment_return_workspace_refresh',
                            requested_workspace_id: requestedWorkspaceId,
                            current_workspace_id: currentWorkspaceId,
                        });
                        console.error('[ModalProvider] Failed to refresh workspaces while waiting for payment return workspace', error);
                    }
                }
                return;
            }

            hideGlobalLoading();

            if (handledPaymentResultKeyRef.current === paymentResultKey) {
                return;
            }
            handledPaymentResultKeyRef.current = paymentResultKey;

            if (pay_result === 'success') {
                const latestProfile = profileRef.current;
                const latestUserBilling = userBillingRef.current;
                const paymentSuccessKind = resolveProductPaymentSuccessKind(latestProfile);
                const isOnboarding = getRouteBehavior(window.location.pathname).pageKind === 'onboarding';
                try {
                    const successPlanCode = paymentReturn.planCode;
                    const successPlanName = paymentReturn.planName;
                    const verifyAuthWorkspaceId = requestedWorkspaceId || currentWorkspaceId;
                    const paymentAlreadyVerified = paymentReturn.checked;
                    const optimisticProfile = applyPaymentReturnPlanToProfile(
                        latestProfile,
                        successPlanCode,
                        successPlanName,
                        paymentReturn.billingCycle,
                    );

                    if (!cancelled && optimisticProfile) {
                        setProfile(optimisticProfile);
                        latestUserBilling.applyWorkspacePlan(optimisticProfile.workspacePlan);
                    }
                    if (!cancelled && verifyAuthWorkspaceId) {
                        applyWorkspaceSubscriptionPlan(
                            verifyAuthWorkspaceId,
                            successPlanCode,
                            successPlanName,
                            paymentReturn.billingCycle,
                        );
                    }

                    const openPaymentSuccessModal = () => {
                        if (cancelled || isOnboarding) {
                            return;
                        }

                        openModal('paymentSuccess', {
                            data: {
                                kind: paymentSuccessKind,
                                planCode: paymentReturn.planCode,
                                planName: paymentReturn.planName,
                            },
                        });
                    };

                    const refreshPaymentSuccessState = async () => {
                        const verifyAuth = applyPaymentReturnPlanToProfile(await refreshProfileFromServer({
                            workspaceId: verifyAuthWorkspaceId,
                            includeSetupCardStatus: Boolean(verifyAuthWorkspaceId),
                        }), successPlanCode, successPlanName, paymentReturn.billingCycle);
                        if (!cancelled && verifyAuth) {
                            setProfile(verifyAuth);
                            latestUserBilling.applyWorkspacePlan(verifyAuth.workspacePlan);
                        }
                        await Promise.allSettled([
                            latestUserBilling.refreshAll(),
                            refreshWorkspaces({
                                silent: true,
                                skipEmptyRedirect: true,
                                preferredWorkspaceId: requestedWorkspaceId || undefined,
                            }),
                        ]);
                        if (!cancelled && verifyAuthWorkspaceId) {
                            applyWorkspaceSubscriptionPlan(
                                verifyAuthWorkspaceId,
                                successPlanCode,
                                successPlanName,
                                paymentReturn.billingCycle,
                            );
                        }
                    };

                    if (paymentAlreadyVerified) {
                        openPaymentSuccessModal();
                        window.dispatchEvent(new CustomEvent('billing:payment-return-success', {
                            detail: paymentReturn,
                        }));
                        void refreshPaymentSuccessState().catch((error) => {
                            logSentryEvent('payment.return.background_refresh_failed', {
                                area: 'payment',
                                requested_workspace_id: requestedWorkspaceId,
                                current_workspace_id: currentWorkspaceId,
                            }, 'error');
                            captureSentryError(error, {
                                area: 'payment',
                                flow: 'payment_return_background_refresh',
                                requested_workspace_id: requestedWorkspaceId,
                                current_workspace_id: currentWorkspaceId,
                            });
                            console.error('[ModalProvider] Failed to background refresh billing state after verified payment success', error);
                        });
                    } else {
                        await refreshPaymentSuccessState();
                        openPaymentSuccessModal();
                        window.dispatchEvent(new CustomEvent('billing:payment-return-success', {
                            detail: paymentReturn,
                        }));
                    }
                    logSentryEvent('payment.return.success_refreshed', {
                        area: 'payment',
                        requested_workspace_id: requestedWorkspaceId,
                        current_workspace_id: currentWorkspaceId,
                        payment_checked: paymentAlreadyVerified,
                    });
                } catch (error) {
                    logSentryEvent('payment.return.billing_refresh_failed', {
                        area: 'payment',
                        requested_workspace_id: requestedWorkspaceId,
                        current_workspace_id: currentWorkspaceId,
                    }, 'error');
                    captureSentryError(error, {
                        area: 'payment',
                        flow: 'payment_return_billing_refresh',
                        requested_workspace_id: requestedWorkspaceId,
                        current_workspace_id: currentWorkspaceId,
                    });
                    console.error('[ModalProvider] Failed to refresh billing state after payment success', error);
                }

                if (cancelled) return;

                if (shouldFinalizeOnboardingPaymentSuccess(
                    window.location.pathname,
                    pay_result,
                    currentWorkspaceId
                )) {
                    switchWorkspace(currentWorkspaceId as string, {
                        params: buildOnboardingPaymentSuccessParams(currentWorkspaceId as string),
                        replace: true,
                        clearParams: ['pay_result', 'payment_checked', 'planCode', 'planName', 'billingCycle', 'workspace_id']
                    });
                    return;
                }

                if (isOnboarding) {
                    showGlobalLoading();
                }

                clearPaymentReturnWorkspaceId();
            }

            if (cancelled) return;

            if (shouldClearPayResultImmediately(window.location.pathname, pay_result)) {
                setSearchParams(removeSearchParams(
                    searchParams,
                    [...PAYMENT_RESULT_PARAMS_TO_CLEAR],
                ), { replace: true });
            }

            if (pay_result === 'cancel') {
                logSentryEvent('payment.return.cancelled', {
                    area: 'payment',
                    requested_workspace_id: requestedWorkspaceId,
                    current_workspace_id: currentWorkspaceId,
                }, 'warn');
                clearPaymentReturnWorkspaceId();
            }
        };

        handlePaymentResult();

        return () => {
            cancelled = true;
        };
    }, [
        searchParams,
        openModal,
        setSearchParams,
        showGlobalLoading,
        hideGlobalLoading,
        isLoadingWorkspaces,
        refreshWorkspaces,
        applyWorkspaceSubscriptionPlan,
        currentWorkspace?.id,
        switchWorkspace
    ]);

    useEffect(() => {
        const inviteReturn = readInviteSuccessReturnParams(searchParams);
        if (!inviteReturn.success) {
            handledInviteSuccessKeyRef.current = null;
            return;
        }

        const requestedWorkspaceId = inviteReturn.workspaceId;
        const currentWorkspaceId = currentWorkspace?.id ?? null;
        const workspaceAction = resolveInviteSuccessWorkspaceAction(
            inviteReturn.success,
            requestedWorkspaceId,
            currentWorkspaceId,
            isLoadingWorkspaces,
        );
        const inviteSuccessKey = [
            window.location.pathname,
            requestedWorkspaceId || '',
            currentWorkspaceId || '',
        ].join('|');

        logSentryEvent('invite.return.detected', {
            area: 'invite',
            requested_workspace_id: requestedWorkspaceId,
            current_workspace_id: currentWorkspaceId,
            workspace_action: workspaceAction,
        });

        if (workspaceAction === 'wait') {
            showGlobalLoading();
            void refreshWorkspaces({
                silent: true,
                skipEmptyRedirect: true,
                preferredWorkspaceId: requestedWorkspaceId || undefined,
            }).catch((error) => {
                logSentryEvent('invite.return.workspace_refresh_failed', {
                    area: 'invite',
                    requested_workspace_id: requestedWorkspaceId,
                    current_workspace_id: currentWorkspaceId,
                }, 'error');
                captureSentryError(error, {
                    area: 'invite',
                    flow: 'invite_success_workspace_refresh',
                    requested_workspace_id: requestedWorkspaceId,
                    current_workspace_id: currentWorkspaceId,
                });
                console.error('[ModalProvider] Failed to refresh workspaces after invite success', error);
            });
            return;
        }

        hideGlobalLoading();

        if (handledInviteSuccessKeyRef.current === inviteSuccessKey) {
            return;
        }
        handledInviteSuccessKeyRef.current = inviteSuccessKey;

        openModal('inviteSuccess');
        setSearchParams(removeSearchParams(
            searchParams,
            [...INVITE_SUCCESS_PARAMS_TO_CLEAR],
        ), { replace: true });
    }, [
        searchParams,
        openModal,
        setSearchParams,
        showGlobalLoading,
        hideGlobalLoading,
        isLoadingWorkspaces,
        refreshWorkspaces,
        currentWorkspace?.id,
    ]);

    // 处理 Shopify store URL 参数
    useEffect(() => {
        const url = new URL(location.href);
        const storeUrl = url.searchParams.get('shop') || '';
        const storeToken = url.searchParams.get('shopify_token') || '';
        if (storeUrl && storeToken) {
            setStores(prev => [...prev.filter(v => v.storeUrl !== storeUrl), { storeUrl, storeToken }]);
        }
    }, [setStores]);

    // 处理 setting_modal URL 参数 - 打开 Settings modal 并指定 activeTab
    useEffect(() => {
        const settingModal = searchParams.get('setting_modal');
        const requestedWorkspaceId = searchParams.get('workspace_id');
        const connectProvider = searchParams.get('connect');

        if (settingModal) {
            const requiresWorkspace = Boolean(requestedWorkspaceId)
                || WORKSPACE_SCOPED_SETTING_TABS.has(settingModal);

            if (requiresWorkspace && (isLoadingWorkspaces || !currentWorkspace?.id)) {
                return;
            }

            if (requestedWorkspaceId && requestedWorkspaceId !== currentWorkspace?.id) {
                switchWorkspace(requestedWorkspaceId, {
                    preservePath: true,
                    replace: true,
                });
                return;
            }

            // 打开 profile modal 并设置指定的 activeTab
            openModal('profile', {
                activeTab: settingModal,
                data: connectProvider ? {
                    connectProvider,
                    connectRequestId: `${Date.now()}`,
                } : undefined,
            });
            replaceSearchParams((params) => {
                params.delete('setting_modal');
                params.delete('connect');
                params.delete('workspace_id');
            });
        }
    }, [searchParams, openModal, currentWorkspace?.id, isLoadingWorkspaces, replaceSearchParams, switchWorkspace]);

    useEffect(() => {
        const paymentModal = searchParams.get('payment_modal');
        const requestedWorkspaceId = searchParams.get('workspace_id');

        if (paymentModal === 'pricing') {
            if (requestedWorkspaceId && (isLoadingWorkspaces || requestedWorkspaceId !== currentWorkspace?.id)) {
                return;
            }

            openModal('pricing');
            replaceSearchParams((params) => {
                params.delete('payment_modal');
            });
        }
    }, [searchParams, openModal, currentWorkspace?.id, isLoadingWorkspaces, replaceSearchParams]);

    const contextValue: ModalState = {
        modals,
        openModal,
        closeModal,
        toggleModal,
        updateModal,
        isModalOpen,
        getModalConfig,
    };

    return contextValue;
};

// Modal Render Component - 接收 modal state 作为 props
interface ModalRenderProps {
    modalState: ModalState;
}

export const ModalRender: React.FC<ModalRenderProps> = ({ modalState }) => {
    const pricingConfig = modalState.modals['pricing'] || { isOpen: false };
    const welcomeConfig = modalState.modals['welcome'] || { isOpen: false };
    const inviteSuccessConfig = modalState.modals['inviteSuccess'] || { isOpen: false };
    const paymentSuccessConfig = modalState.modals['paymentSuccess'] || { isOpen: false };
    const connectShopifyConfig = modalState.modals['connectShopify'] || { isOpen: false };
    const forNextPulseConfig = modalState.modals['forNextPulse'] || { isOpen: false };

    return (
        <>
            {/* Deprecated 给自定义登录使用的 */}
            <ConnectShopify
                isOpen={connectShopifyConfig.isOpen}
                onClose={() => modalState.closeModal('connectShopify')}
            />
            <PricingModal
                isOpen={pricingConfig.isOpen}
                onClose={() => modalState.closeModal('pricing')}
                fisrt={false}
                workspaceId={pricingConfig.data?.workspaceId}
                preferredTeamOfferCode={pricingConfig.data?.preferredTeamOfferCode}
            />
            <PaySuccessModal
                isOpen={welcomeConfig.isOpen}
                checking={Boolean(welcomeConfig.data?.checking)}
                onClose={() => modalState.closeModal('welcome')}
            />
            <InviteSuccessModal
                isOpen={inviteSuccessConfig.isOpen}
                onClose={() => modalState.closeModal('inviteSuccess')}
            />
            <PaymentSuccessModal
                isOpen={paymentSuccessConfig.isOpen}
                kind={paymentSuccessConfig.data?.kind}
                planCode={paymentSuccessConfig.data?.planCode}
                planName={paymentSuccessConfig.data?.planName}
                onClose={() => modalState.closeModal('paymentSuccess')}
            />
            {/* Deprecated 给免费套餐引导支付的 */}
            <ForNextPulseModal
                isOpen={forNextPulseConfig.isOpen}
                onClose={() => modalState.closeModal('forNextPulse')}
            />
        </>
    );
};
